import { ReactNode } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';

import { Ordinal } from 'components/base/Ordinal';

import './DetailItem.scss';

interface DetailItemProps {
  /** 1-based position shown as an Ordinal badge. Omit for an unnumbered item. */
  readonly index?: number;
  /** Opaque drag-handle node injected by `DetailList` when the list is ordered. */
  readonly dragHandle?: ReactNode;
  /** Extra controls placed before the reorder/remove buttons. */
  readonly actions?: ReactNode;
  /**
   * Explicit reorder controls (alongside drag). When either is set, up/down buttons render; pass `undefined` for the one at
   * the boundary (first item -> no `onMoveUp`, last -> no `onMoveDown`) and it shows disabled.
   */
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  /** When set, a trash button calls this. */
  readonly onRemove?: () => void;
  readonly removeDisabled?: boolean;
  readonly removeLabel?: string;
  /** The single-line content, laid out in a stretched flex row; the caller sizes its columns (e.g. `flex-fill`). */
  readonly children: ReactNode;
}

/**
 * A single-line item in the "details" shell — DetailCard's collapsed look (same surface / radius / shadow) but the content
 * *is* the row: no header band, subtitle, chevron or body. Carries the shared item chrome (drag handle, ordinal, reorder
 * up/down, extra actions, remove) so callers only supply the inline content. Pairs with `DetailList` like `DetailCard`, for
 * compact collections (retention policies, handles, …).
 */
export const DetailItem = observer(
  ({ index, dragHandle, actions, onMoveUp, onMoveDown, onRemove, removeDisabled = false, removeLabel = 'Remove', children }: DetailItemProps) => {
    const reorderable = onMoveUp !== undefined || onMoveDown !== undefined;
    return (
      <Card className="detail-item">
        <div className="detail-item__row">
          {dragHandle}
          {index !== undefined && <Ordinal value={index} />}
          <div className="detail-item__content">{children}</div>
          <span className="detail-item__actions">
            {actions}
            {reorderable && (
              <span className="detail-item__reorder">
                <Button variant="link" className="detail-item__move" aria-label="Move up" title="Move up" onClick={onMoveUp} disabled={onMoveUp === undefined}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </Button>
                <Button
                  variant="link"
                  className="detail-item__move"
                  aria-label="Move down"
                  title="Move down"
                  onClick={onMoveDown}
                  disabled={onMoveDown === undefined}
                >
                  <FontAwesomeIcon icon={faChevronDown} />
                </Button>
              </span>
            )}
            {onRemove !== undefined && (
              <Button variant="link" className="detail-item__remove" aria-label={removeLabel} title={removeLabel} onClick={onRemove} disabled={removeDisabled}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </span>
        </div>
      </Card>
    );
  }
);
